import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../auth/CurrentUserProvider';
import { DetailPageSkeleton } from '../../components/DetailPageSkeleton';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { OwnerAssetsTab } from './OwnerAssetsTab';
import { OwnerDetailsForm } from './OwnerDetailsForm';
import { OwnerHistoryTab } from './OwnerHistoryTab';
import { OWNER_TAB_LABELS, OWNER_TABS, ownerTabPath, type OwnerTab } from './owner-tabs';
import { useOwnerDetail } from './use-owner-detail';
import { withinSection } from '../../layouts/section-trail';
import { FLEET_TAB_LABELS, fleetTabPath } from '../fleet/fleet-tabs';

const OWNER_TRAIL = withinSection('fleet', { label: FLEET_TAB_LABELS.owners, to: fleetTabPath('owners') });

/**
 * An owner's own page: fields on one tab, its trucks and trailers and history on the others —
 * an owner can hold both (gitaumoses4/chukua#15). `owner.manage` mutates the record; `owner.view`
 * alone sees everything read-only (CLAUDE.md: nav-hiding and disabled controls are UX only, the
 * server remains the authority).
 */
export function OwnerDetailPage({ tab }: { tab: OwnerTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, updateError, saving, update } = useOwnerDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('owner.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading owner" breadcrumbs={OWNER_TRAIL} tabs={OWNER_TABS.length} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Owner not found" description="This owner doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this owner" description="Something went wrong loading this owner. Try reloading the page." />;
  }

  const { owner } = state;

  return (
    <>
      <PageHeader breadcrumbs={OWNER_TRAIL} title={owner.name} description={owner.isCompany ? 'The company' : 'Investor'} />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(ownerTabPath(owner.id, key as OwnerTab))}
        items={[
          {
            key: 'details',
            label: OWNER_TAB_LABELS.details,
            children: <OwnerDetailsForm owner={owner} canManage={canManage} error={updateError} saving={saving} onSave={update} />,
          },
          {
            key: 'assets',
            label: OWNER_TAB_LABELS.assets,
            children: (
              <OwnerAssetsTab
                trucksCurrent={owner.trucksCurrent}
                trucksPast={owner.trucksPast}
                trailersCurrent={owner.trailersCurrent}
                trailersPast={owner.trailersPast}
              />
            ),
          },
          { key: 'history', label: OWNER_TAB_LABELS.history, children: <OwnerHistoryTab ownerId={owner.id} /> },
        ]}
      />
    </>
  );
}
